// frontend/src/components/AGTFit/AGTFit.js
import React, { useState, useEffect, useRef } from 'react';
import { 
  Box,
  useTheme
} from '@mui/material';
import Config from './config/Config';
import AppleSpinner from '../common/AppleSpinner';
import FitProfile from './fit/FitProfile';
import WorkoutMode from './workout/WorkoutMode';
import QuickStart from './workout/QuickStart';
import agtfitService from './service/agtfitService';
import AGTFloatingChat from './utils/AGTFloatingChat';
import { AGTFitProProvider } from './context/AGTFitProContext';

const AGTFit = ({ user, onUpdateUser }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [configuredUser, setConfiguredUser] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [workoutMode, setWorkoutMode] = useState(null); // null, 'selection', 'active'
  
  // Referencias para controlar la inicialización
  const isInitialized = useRef(false);
  const prevUserIdRef = useRef(user?.uid);

  // Función para cargar datos del usuario desde localStorage
  const loadUserFromStorage = () => {
    const fitGoal = localStorage.getItem('agtfit_goal');
    const fitApproach = localStorage.getItem('agtfit_approach');
    const fitCalories = localStorage.getItem('agtfit_calories');
    const fitMacrosStr = localStorage.getItem('agtfit_macros');
    
    if (fitGoal && fitApproach && fitCalories) {
      let fitMacros = null;
      try {
        fitMacros = JSON.parse(fitMacrosStr);
      } catch (e) {
        console.error('Error parsing macros from localStorage', e);
      }
      
      return {
        goal: fitGoal,
        approach: fitApproach,
        calculatedCalories: parseInt(fitCalories),
        macros: fitMacros,
        hasProfile: true
      };
    }
    return null;
  };

  useEffect(() => {
    // Solo realizar la inicialización si no se ha hecho antes o si cambia el ID del usuario
    if (!isInitialized.current || (user && user.uid && user.uid !== prevUserIdRef.current)) {
      isInitialized.current = true;
      prevUserIdRef.current = user?.uid;
      
      const initUser = async () => {
        try {
          // Verificar primero si tenemos datos en localStorage para evitar llamadas innecesarias
          const storedUser = loadUserFromStorage();
          
          if (storedUser) {
            setConfiguredUser(storedUser);
            setShowConfig(false);
            setLoading(false);
            return; // Salir temprano si tenemos datos en caché
          }
          
          // Solo si no hay datos en localStorage, intentar obtener del backend
          const profileResponse = await agtfitService.getProfile();
          
          if (profileResponse.success && profileResponse.data) {
            setConfiguredUser({
              ...profileResponse.data,
              hasProfile: true
            });
            setShowConfig(false);
          } else {
            setConfiguredUser(null);
            setShowConfig(true);
          }
        } catch (error) {
          console.error('Error al cargar perfil:', error);
          
          // Intentar cargar desde localStorage como fallback
          const storedUser = loadUserFromStorage();
          
          if (storedUser) {
            setConfiguredUser(storedUser);
            setShowConfig(false);
          } else {
            setConfiguredUser(null);
            setShowConfig(true);
          }
        } finally {
          setLoading(false);
        }
      };
      
      initUser();
    }
  }, [user]);

  // Manejar actualización del perfil desde el componente Config
  const handleUserUpdate = (updatedUser) => {
    setConfiguredUser(updatedUser);
    setShowConfig(false);
    
    if (onUpdateUser) {
      onUpdateUser(updatedUser);
    }
  };

  // Manejar edición del perfil (volver a mostrar Config)
  const handleEditProfile = () => {
    setShowConfig(true);
  };
  
  // Manejar inicio del modo entrenamiento
  const handleStartWorkoutMode = () => {
    setWorkoutMode('selection');
  };
  
  // Manejar inicio de entrenamiento vacío
  const handleStartEmptyWorkout = () => {
    setWorkoutMode('active');
  };
  
  // Manejar salida del modo entrenamiento
  const handleExitWorkoutMode = () => {
    if (workoutMode === 'active') {
      setWorkoutMode('selection'); // Volver a QuickStart desde WorkoutMode
    } else {
      setWorkoutMode(null); // Volver a FitProfile desde QuickStart
    }
  };
  
  // Manejar finalización del entrenamiento
  const handleFinishWorkout = (workoutData) => {
    // Aquí se procesaría la información del entrenamiento
    setWorkoutMode(null);
  };

  // Verificar si estamos en modo perfil (no en configuración ni entrenamiento)
  const isProfileMode = !showConfig && workoutMode === null;

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100%',
        bgcolor: theme.palette.actualMode === 'wallpaper'
          ? 'transparent'
          : (theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF'),
      }}>
        <AppleSpinner sizeDesktop={60} sizeMobile={40} />
      </Box>
    );
  }

  // Renderizar el contenido según el estado
  const renderContent = () => {
    if (showConfig) {
      return (
        <Config 
          onUpdateUser={handleUserUpdate} 
          currentUser={configuredUser} 
        />
      );
    }
    
    if (workoutMode === 'selection') {
      return (
        <QuickStart 
          onStartEmptyWorkout={handleStartEmptyWorkout}
          onCreateRoutine={() => {}}
          onExploreRoutines={() => {}}
          onBack={handleExitWorkoutMode} // Añadir esta prop
        />
      );
    }
    
    if (workoutMode === 'active') {
      return (
        <WorkoutMode 
          onExit={handleExitWorkoutMode}
          onFinish={handleFinishWorkout}
        />
      );
    }
    
    return (
      <FitProfile 
        user={{...configuredUser, displayName: user.displayName}} 
        onEditProfile={handleEditProfile}
        onStartWorkout={handleStartWorkoutMode}
      />
    );
  };

  // Contenedor principal con scroll adecuado y color de fondo
  return (
    <AGTFitProProvider>
      <Box sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'auto',
        bgcolor: theme.palette.actualMode === 'wallpaper'
          ? 'transparent'
          : (theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF'),
      }}>
        <Box sx={{
          width: '100%',
          pb: { xs: '80px', sm: '100px', md: '120px' },
        }}>
          {renderContent()}
        </Box>
        {/* Solo renderizar AGTFloatingChat cuando estamos en modo perfil */}
        {isProfileMode && <AGTFloatingChat />}
      </Box>
    </AGTFitProProvider>
  );
};

export default AGTFit;